import { useMemo } from 'react';
import { useAppData } from './useAppData';

/**
 * Hook para detectar vendedores con clientes que tienen cuotas vencidas
 * Retorna información sobre cuotas vencidas por vendedor
 */
export function useAlertaCuotasVencidas() {
  const { lots, expenses } = useAppData();

  const alertasPorVendedor = useMemo(() => {
    const alertas: Record<string, {
      vendedorId: string;
      vendedorNombre: string;
      clientesConCuotasVencidas: Array<{
        comprador: string;
        lotes: string[];
        cuotasVencidas: Array<{
          cuotaNumero: number;
          fechaVencimiento: string;
          montoCuota: number;
          diasVencida: number;
        }>;
        totalDeuda: number;
      }>;
      totalCuotasVencidas: number;
      totalMontoVencido: number;
    }> = {};

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0); // Medianoche para comparación precisa

    // Función auxiliar para obtener abonos sincronizados (EXACTA de ReporteCobrosMensuales.tsx)
    const getAbonosSincronizadosRobusto = (lote: any, expenses: any[]) => {
      const abonosLote = Array.isArray(lote.payments) ? lote.payments.filter((p: any) => p.type === 'abono') : [];
      
      // Obtener IDs de lotes para este grupo
      const lotesIds = Array.isArray(lote.idLotes) ? lote.idLotes : [lote.id];
      
      const abonosMovimientos = expenses.filter((mov: any) => {
        if (mov.tipo !== 'abono' || !mov.lotes || !mov.automatic) return false;
        const lotesMovimiento = Array.isArray(mov.lotes) ? mov.lotes : [mov.lotes];
        return lotesMovimiento.some((loteId: string) => lotesIds.includes(String(loteId)));
      }).map((mov: any) => ({
        amount: mov.monto,
        date: mov.fecha,
        type: 'abono',
        cuotaNumero: mov.cuotaNumero,
        cuotaFechaVencimiento: mov.cuotaFechaVencimiento,
        origen: 'movimientos'
      }));
      
      return [...abonosLote, ...abonosMovimientos];
    };

    // Procesar todos los lotes a crédito
    const lotesCredito = lots.filter((lote: any) => 
      lote.status === 'crédito' || lote.paymentType === 'pagos'
    );

    lotesCredito.forEach((lote: any) => {
      const vendedorId = lote.sellerId || lote.seller || '';
      const vendedorNombre = lote.sellerName || vendedorId;
      const comprador = lote.buyerName || lote.comprador || '';
      const numberOfPayments = lote.numberOfPayments || lote.numeroPagos || lote.numPayments || 0;
      
      if (!vendedorId || !comprador || numberOfPayments === 0) return;

      // Calcular cuotas del lote
      const totalValue = lote.totalValue || parseFloat(lote.valorTotal) || 0;
      const enganche = (Array.isArray(lote.payments) ? lote.payments : [])
        .find((p: any) => p.type === 'enganche')?.amount || 0;
      const saldoCredito = totalValue - enganche;
      const cuotaMensual = numberOfPayments > 0 ? saldoCredito / numberOfPayments : 0;
      const fechaVenta = new Date(lote.saleDate || lote.fechaVenta || new Date());

      // Obtener abonos sincronizados para este lote
      const abonosSincronizados = getAbonosSincronizadosRobusto(lote, expenses);
      
      // Debug: log para este lote específico
      if (comprador === 'Juan Choc Rax') {
        console.log('🔍 DEBUG - Analizando Juan Choc Rax:', {
          vendedorId,
          vendedorNombre,
          comprador,
          numberOfPayments,
          totalValue,
          enganche,
          saldoCredito,
          cuotaMensual,
          abonosSincronizados: abonosSincronizados.length,
          lotesIds: Array.isArray(lote.idLotes) ? lote.idLotes : [lote.id]
        });
      }

      const cuotasVencidas: Array<{
        cuotaNumero: number;
        fechaVencimiento: string;
        montoCuota: number;
        diasVencida: number;
      }> = [];

      // Verificar cada cuota usando EXACTA lógica de ReporteCobrosMensuales.tsx
      const todayMid = new Date();
      todayMid.setHours(0, 0, 0, 0);

      for (let i = 1; i <= numberOfPayments; i++) {
        const fechaVenc = new Date(fechaVenta);
        fechaVenc.setMonth(fechaVenc.getMonth() + i);
        const fechaStr = fechaVenc.toISOString().split('T')[0];
        
        // CALCULAR PAGOS USANDO ABONOS SINCRONIZADOS (incluye manuales y automáticos)
        const pagosCuota = abonosSincronizados.filter((p: any) => {
          const sameCuota = String(p.cuotaNumero) === String(i);
          // Tolerar pequeñas diferencias de fecha de vencimiento
          const pVenc = (p.cuotaFechaVencimiento || '').toString().slice(0, 10);
          const sameVenc = !p.cuotaFechaVencimiento || pVenc === fechaStr;
          return sameCuota && sameVenc;
        });
        
        const pagado = pagosCuota.reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
        const saldo = Math.max(cuotaMensual - pagado, 0);

        // Debug para Juan Choc Rax
        if (comprador === 'Juan Choc Rax') {
          console.log(`💰 DEBUG - Cuota ${i}:`, {
            fechaStr,
            cuotaMensual,
            pagosCuota: pagosCuota.length,
            pagado,
            saldo,
            fechaVencPasada: fechaVenc < todayMid,
            fechaVenc: fechaVenc.toISOString(),
            todayMid: todayMid.toISOString()
          });
        }

        // Si tiene saldo pendiente y la fecha ya pasó, está vencida
        if (saldo > 0.0001 && fechaVenc < todayMid) {
          const diasVencida = Math.floor((todayMid.getTime() - fechaVenc.getTime()) / (1000 * 60 * 60 * 24));
          cuotasVencidas.push({
            cuotaNumero: i,
            fechaVencimiento: fechaStr,
            montoCuota: saldo, // Solo el saldo pendiente
            diasVencida
          });
        }
      }

      // Si tiene cuotas vencidas, agregarlo a las alertas
      if (cuotasVencidas.length > 0) {
        if (!alertas[vendedorId]) {
          alertas[vendedorId] = {
            vendedorId,
            vendedorNombre,
            clientesConCuotasVencidas: [],
            totalCuotasVencidas: 0,
            totalMontoVencido: 0
          };
        }

        const lotesIds = Array.isArray(lote.idLotes) ? lote.idLotes : [lote.id];
        const totalDeuda = cuotasVencidas.reduce((sum, c) => sum + c.montoCuota, 0);

        // Buscar si ya existe un cliente con el mismo nombre (para agrupar lotes)
        const clienteExistente = alertas[vendedorId].clientesConCuotasVencidas
          .find(c => c.comprador === comprador);

        if (clienteExistente) {
          // Agregar lotes y cuotas al cliente existente
          clienteExistente.lotes.push(...lotesIds);
          clienteExistente.cuotasVencidas.push(...cuotasVencidas);
          clienteExistente.totalDeuda += totalDeuda;
        } else {
          // Nuevo cliente
          alertas[vendedorId].clientesConCuotasVencidas.push({
            comprador,
            lotes: lotesIds,
            cuotasVencidas,
            totalDeuda
          });
        }

        alertas[vendedorId].totalCuotasVencidas += cuotasVencidas.length;
        alertas[vendedorId].totalMontoVencido += totalDeuda;
      }
    });

    return alertas;
  }, [lots, expenses]);

  // Función para verificar si un vendedor específico tiene cuotas vencidas
  const tieneVendedorCuotasVencidas = (vendedorId: string): boolean => {
    return !!alertasPorVendedor[vendedorId];
  };

  // Función para obtener resumen de alertas de un vendedor específico
  const getAlertaVendedor = (vendedorId: string) => {
    return alertasPorVendedor[vendedorId] || null;
  };

  // Función para obtener estadísticas globales
  const getEstadisticasGlobales = () => {
    const vendedoresConAlertas = Object.keys(alertasPorVendedor).length;
    const totalClientesAfectados = Object.values(alertasPorVendedor)
      .reduce((sum, alerta) => sum + alerta.clientesConCuotasVencidas.length, 0);
    const totalCuotasVencidas = Object.values(alertasPorVendedor)
      .reduce((sum, alerta) => sum + alerta.totalCuotasVencidas, 0);
    const totalMontoVencido = Object.values(alertasPorVendedor)
      .reduce((sum, alerta) => sum + alerta.totalMontoVencido, 0);

    return {
      vendedoresConAlertas,
      totalClientesAfectados,
      totalCuotasVencidas,
      totalMontoVencido
    };
  };

  return {
    alertasPorVendedor,
    tieneVendedorCuotasVencidas,
    getAlertaVendedor,
    getEstadisticasGlobales
  };
}
